import { Injectable, WritableSignal, signal } from '@angular/core';
import { ArquivoUpload } from '../../../../shared/constants/upload-documentos/arquivo.interface';
import { DadosProponenteSignals } from './models/dados-titular-signals.interface';
import { InformacoesDadosCadastraisTradicional } from './models/informacoes-dados-cadastrais-tradicional.interface';
import { Proponente, Telefone } from '../../../pages/tradicional/proponentes/hub-proponentes/models/proponente-idpessoa-response.interface';
import { FormularioDadosPessoaisTradicional } from './models/formulario-dados-pessoais-tradicional.interface';
import { FormularioDadosEnderecoTradicional } from './models/formulario-dados-endereco-tradicional.interface';
import { LISTA_ESTADOS_BRASIL } from '../../../../shared/constants/dados-cadastrais/estados-brasileiro.const';
import { FormularioDadosComplementares } from './models/formulario-dados-complementares-tradicional.interface';
import { Autocomplete } from 'src/app/shared/interfaces/dados-complementares/autocomplete.interface';
import { Pais } from 'src/app/shared/interfaces/dados-complementares/paises-response.interface';
import { PAISES } from 'src/app/shared/constants/dados-complementares/paises.const';
import { Endereco } from 'src/app/shared/interfaces/dados-endereco/endereco.interface';
import { LISTA_OPCAO_ORGAO_EMISSOR } from 'src/app/shared/constants/dados-cadastrais/opcao-orgao-emissor.const';
import { ASSINATURA } from 'src/app/abertura-conta/pages/tradicional/assinatura/tipo-assinatura/tipo-assinatura/tipo-assinatura.component';
import { ProcuradorEmbedded } from 'src/app/shared/interfaces/procurador/procurador.interface';
import { sanitizeDocumentNumber } from 'src/app/shared/validators/sanitize-document-number.util';

/**
 * Serviço responsável por gerenciar os dados do proponente tradicional.
 */
@Injectable({ providedIn: 'root' })
export class StateDadosProponenteTradicionalService {

  /**
   * Sinal para armazenar as informações cadastrais do proponente tradicional.
   */
  public setInformacoesDadosCadastraisTradicional: WritableSignal<InformacoesDadosCadastraisTradicional>
    = signal<InformacoesDadosCadastraisTradicional>({
      dadosProponente: null,
      idProposta: null,
      isNovoCadastro: null,
      tituloDadosCadastrais: null,
      documentoProponente: null,
    });

  /**
   * Sinal para armazenar a procuração.
   */
  public setProcuracao = signal<ArquivoUpload>({ arquivo: null, extensao: null, documento: null });

  /**
   * Sinal para armazenar os dados do titular.
   */
  public DadosTitular = signal<DadosProponenteSignals | null>(null);

  /**
   * Sinal para armazenar os dados dos cotitulares.
   */
  public DadosCotitular = signal<Array<DadosProponenteSignals>>([]);

  public tipoAssinatura = signal<ASSINATURA | null>(null);

  public listprofissao: Autocomplete[] = [];

  public listaOrigemRenda: Autocomplete[] = [];

  /**
   * Mapeia os dados pessoais do formulário para um objeto Proponente.
   * @param formularioDadosPessoais - Dados do formulário contendo informações pessoais.
   * @returns Um objeto Proponente com os dados pessoais atualizados.
   */
  public MapDadosPessoaisProponente(formularioDadosPessoais: FormularioDadosPessoaisTradicional): Proponente {
    const dadoProponente = this.setInformacoesDadosCadastraisTradicional();
    let dadosProponenteExistentes = dadoProponente?.dadosProponente || {} as Proponente;

    let documentosSalvos = [];

    if (dadosProponenteExistentes && dadosProponenteExistentes.documentos) {
      documentosSalvos = dadosProponenteExistentes.documentos.filter(doc => doc.tipo == 12213);
    }

    documentosSalvos.push(
      {
        tipo: formularioDadosPessoais.documentoIdentificacao ? parseInt(formularioDadosPessoais.documentoIdentificacao) : null,
        numero: sanitizeDocumentNumber(formularioDadosPessoais.numeroDocumento || ''),
        dataEmissao: this.formatDate(formularioDadosPessoais.dataEmissao) || "",
        orgaoEmissor: this.searchSiglaEmissor(formularioDadosPessoais.orgaoEmissor || ""),
        uf: formularioDadosPessoais.ufEmissao || "",
        pais: "BR",
        identificador: 521,
      }
    )
    const dadosAtualizados: Partial<Proponente> = {
      nome: formularioDadosPessoais.nome || "",
      nomeSocial: formularioDadosPessoais.nomeSocial || "",
      sexo: formularioDadosPessoais.sexo || "",
      cpf: formularioDadosPessoais.cpf || "",
      dataNascimento: formularioDadosPessoais.dataNascimento ? this.formatDate(formularioDadosPessoais.dataNascimento) : "",
      nacionalidade: formularioDadosPessoais.nacionalidade || 0,
      paisNascimento: formularioDadosPessoais.paisNascimento || "",
      ufNascimento: formularioDadosPessoais.estadoNascimento || "",
      cidadeNascimento: formularioDadosPessoais.cidadeNascimento || "",
      nomeMae: formularioDadosPessoais.nomeMae || "",
      telefone: this.atualizarTelefoneComDDD(formularioDadosPessoais),
      email: formularioDadosPessoais.email || "",
      indicadorAceitaReceberEmail: formularioDadosPessoais.preferenciasContato?.email || false,
      indicadorAceitaReceberSms: formularioDadosPessoais.preferenciasContato?.whatsapp || false,
      estadoCivil: formularioDadosPessoais.estadoCivil ? parseInt(formularioDadosPessoais.estadoCivil) : null,
      conjuge: formularioDadosPessoais.nomeConjugue ? {
        nome: formularioDadosPessoais.nomeConjugue,
        cpf: formularioDadosPessoais.cpfConjugue || ""
      } : null,
      documentos: documentosSalvos

    };

    return { ...dadosProponenteExistentes, ...dadosAtualizados };
  }

  /**
   * Mapeia os dados de endereço do formulário para um objeto Proponente.
   * @param formularioDadosPessoais - Dados do formulário contendo informações de endereço.
   * @returns Um objeto Proponente com os dados de endereço atualizados.
   */
  public mapDadosEnderecoProponente(formularioDadosPessoais: FormularioDadosEnderecoTradicional): Proponente {
    const dadoProponente = this.setInformacoesDadosCadastraisTradicional();
    let dadosProponenteExistentes = dadoProponente?.dadosProponente || {} as Proponente;

    let dadosAtualizados = {
      enderecos: [
        {
          logradouro: formularioDadosPessoais.endereco || "",
          numero: formularioDadosPessoais.numero || "",
          complemento: formularioDadosPessoais.complemento || "",
          bairro: formularioDadosPessoais.bairro || "",
          cep: this.filterCep(formularioDadosPessoais.cep) || "",
          cidade: formularioDadosPessoais.cidade || "",
          uf: this.buscarUFPorNome(formularioDadosPessoais.estado) || "",
          pais: "BR",
          propositos: [1]
        }
      ] as Endereco[]
    };

    dadosAtualizados.enderecos = this.propositoEnderecoComercial(dadosAtualizados.enderecos, formularioDadosPessoais);
    dadosAtualizados.enderecos = this.propositoEnderecoCorrespondencia(dadosAtualizados.enderecos, formularioDadosPessoais);

    return { ...dadosProponenteExistentes, ...dadosAtualizados };
  }

  /**
   * Adiciona o propósito de endereço comercial, se aplicável.
   * @param enderecoPrincipal - Lista de endereços principal.
   * @param formularioDadosPessoais - Dados do formulário contendo informações de endereço.
   * @returns Lista de endereços atualizada com o propósito comercial.
   */
  private propositoEnderecoComercial(enderecoPrincipal: Endereco[], formularioDadosPessoais: FormularioDadosEnderecoTradicional): Endereco[] {
    if (formularioDadosPessoais.enderecoComercial.adicionarEnderecoComercial == 0) {
      let mesmoEndereco = enderecoPrincipal;
      mesmoEndereco[0].propositos.push(2);
      return mesmoEndereco;
    }

    enderecoPrincipal.push({
      logradouro: formularioDadosPessoais.dadosEnderecoComercial.enderecoComercial || "",
      numero: formularioDadosPessoais.dadosEnderecoComercial.numeroComercial || "",
      complemento: formularioDadosPessoais.dadosEnderecoComercial.complementoComercial || "",
      bairro: formularioDadosPessoais.dadosEnderecoComercial.bairroComercial || "",
      cep: this.filterCep(formularioDadosPessoais.dadosEnderecoComercial.cepComercial) || "",
      cidade: formularioDadosPessoais.dadosEnderecoComercial.cidadeComercial || "",
      uf: this.buscarUFPorNome(formularioDadosPessoais.dadosEnderecoComercial.estadoComercial) || "",
      pais: "BR",
      propositos: [2]
    });
    return enderecoPrincipal;
  }

  /**
   * Adiciona o propósito de endereço de correspondência, se aplicável.
   * @param enderecoPrincipal - Lista de endereços principal.
   * @param formularioDadosPessoais - Dados do formulário contendo informações de endereço.
   * @returns Lista de endereços atualizada com o propósito de correspondência.
   */
  private propositoEnderecoCorrespondencia(enderecoPrincipal: Endereco[], formularioDadosPessoais: FormularioDadosEnderecoTradicional): Endereco[] {
    if (formularioDadosPessoais.enderecoCorrespondencia.adicionarEnderecoCorrespondencia == 0) {
      let mesmoEndereco = enderecoPrincipal;
      mesmoEndereco[0].propositos.push(3);
      return mesmoEndereco;
    }

    if (formularioDadosPessoais.enderecoCorrespondencia.adicionarEnderecoCorrespondencia == 1) {
      let mesmoEndereco = enderecoPrincipal;
      mesmoEndereco[1].propositos.push(3);
      return mesmoEndereco;
    }

    enderecoPrincipal.push({
      logradouro: formularioDadosPessoais.dadosEnderecoCorrespondencia.enderecoCorrespondencia || "",
      numero: formularioDadosPessoais.dadosEnderecoCorrespondencia.numeroCorrespondencia || "",
      complemento: formularioDadosPessoais.dadosEnderecoCorrespondencia.complementoCorrespondencia || "",
      bairro: formularioDadosPessoais.dadosEnderecoCorrespondencia.bairroCorrespondencia || "",
      cep: this.filterCep(formularioDadosPessoais.dadosEnderecoCorrespondencia.cepCorrespondencia) || "",
      cidade: formularioDadosPessoais.dadosEnderecoCorrespondencia.cidadeCorrespondencia || "",
      uf: this.buscarUFPorNome(formularioDadosPessoais.dadosEnderecoCorrespondencia.estadoCorrespondencia) || "",
      pais: "BR",
      propositos: [3]
    });
    return enderecoPrincipal;
  }

  /**
 * Mapeia os dados complementares do formulário para um objeto Proponente.
 * 
 * Este método atualiza os dados complementares de um proponente com base nas informações
 * fornecidas no formulário. Ele combina os dados existentes do proponente com os novos
 * dados complementares, retornando um objeto Proponente atualizado.
 * 
 * @param dadosComplementares - Dados do formulário contendo informações complementares.
 * @returns Um objeto Proponente com os dados complementares atualizados.
 */

  public mapDadosComplementaresProponente(dadosComplementares: FormularioDadosComplementares, isParcerias: boolean = false): Proponente {

    const dadoProponente = this.setInformacoesDadosCadastraisTradicional();
    let dadosProponenteExistentes = dadoProponente?.dadosProponente || {} as Proponente;

    let documentosLista = dadoProponente.dadosProponente?.documentos?.filter(doc => doc.tipo !== 12213) || [];
    if (dadosComplementares.residenciaFiscalExterior && dadosComplementares.residenciasExterior.length > 0) {
      const listaPaises = PAISES.data;
      dadosComplementares.residenciasExterior.forEach(residencia => {
        documentosLista.push({
          tipo: 12213, // Tipo de documento para residência fiscal exterior
          identificador: 522, // Identificador do residencia fiscal exterior
          pais: this.buscarPaisSiglaNome(listaPaises, residencia.pais, "siglaPaisDoisCaracteres") as string,
          numero: residencia.nif
        });
      });
    }

    let dadosAtualizados: Record<string, any> = {
      indicadorPossuiPatrimonio: dadosComplementares.patrimonio,
      patrimonio: dadosComplementares.patrimonio && dadosComplementares.valorPatrimonio ? parseFloat(dadosComplementares.valorPatrimonio) : 0,
      renda: dadosComplementares.renda && dadosComplementares.valorRenda ? parseFloat(dadosComplementares.valorRenda) : 0,
      origemRenda: dadosComplementares.origemRenda ? parseInt(this.patchOrigemRenda(dadosComplementares.origemRenda, "value") as string) : null,
      indicadorPossuiOperacaoCambio: dadosComplementares.cambio || false,
      codigoTipoPessoaExpostaPoliticamente: dadosComplementares.politicamenteExposto ? this.mapTipoExposicaoPolitica(dadosComplementares) : null,
      descricaoJustificativaAusenciaRenda: !dadosComplementares.renda ? dadosComplementares.justificativaRenda : null,
      possuiResidenciaFiscalExterior: dadosComplementares.residenciaFiscalExterior || false,
      documentos: documentosLista,
    };

    if (!isParcerias) {
      dadosAtualizados['ocupacao'] = dadosComplementares.profissao ? this.patchProfissao(dadosComplementares.profissao, "value").toString() : null;
    }

    return { ...dadosProponenteExistentes, ...dadosAtualizados };
  }

  /* -------------------- ATUALIZAR DADOS PESSOAIS STATE PROPONENTE --------------------- */

  /**
 * Atualiza os dados de proponentes no signal setInformacoesDadosCadastraisTradicional.
 * @param proponente - Objeto contendo os dados atualizados do proponente.
 */
  public atualizarDadosProponenteDadosCadastraisSelecionado(proponente: Proponente): void {

    this.setInformacoesDadosCadastraisTradicional.update((dadosAtuais) => ({
      ...dadosAtuais,
      dadosProponente: proponente,
    }));
  }

  /**
   * Atualiza o atributo `procurador` embutido do proponente selecionado no estado.
   * Mantém o estado em memória sincronizado com o cadastro/remoção do procurador,
   * garantindo que o card seja exibido ao retornar para a página (RF-021).
   * @param procurador Dados embutidos do procurador ou null para remoção.
   */
  public atualizarProcuradorProponenteSelecionado(procurador: ProcuradorEmbedded | null): void {
    this.setInformacoesDadosCadastraisTradicional.update((dadosAtuais) => {
      if (!dadosAtuais.dadosProponente) return dadosAtuais;

      return {
        ...dadosAtuais,
        dadosProponente: {
          ...dadosAtuais.dadosProponente,
          procurador,
        },
      };
    });
  }

  /**
   * Atualiza os dados de proponentes no signal DadosTitular.
   * @param proponente - Objeto contendo os dados atualizados do proponente.
   */
  public atualizarDadosProponenteTitular(proponente: Proponente): void {
    this.DadosTitular.update((dadosAtuais) => ({
      ...dadosAtuais,
      proponentes: proponente,
    }));
  }

  /**
 * Atualiza os dados de um cotitular específico no array de DadosCotitular.
 * @param index - Índice do cotitular a ser atualizado.
 * @param updatedProponente - Objeto contendo os dados atualizados do proponente.
 */
  public atualizarDadosProponenteCotitular(index: number, updatedProponente: Proponente): void {
    const currentData = this.DadosCotitular();
    if (index >= 0 && index < currentData.length) {
      const updatedCotitular: DadosProponenteSignals = {
        ...currentData[index],
        proponentes: updatedProponente,
      };
      const updatedData = [...currentData];
      updatedData[index] = updatedCotitular;
      this.DadosCotitular.set(updatedData); // Corrigido para usar o array completo
    }
  }


  /**
   * Mapeia o tipo de exposição política com base nos dados complementares fornecidos.
   *
   * Este método verifica as opções de exposição política selecionadas e retorna uma string
   * representando os tipos de exposição política aplicáveis.
   *
   * @param dadosComplementares - Dados do formulário contendo informações de exposição política.
   * @returns Uma string representando os tipos de exposição política ou null se não houver exposição.
   */
  private mapTipoExposicaoPolitica(dadosComplementares: FormularioDadosComplementares): string {
    if (!dadosComplementares.exposicaoPolitica)
      return null;

    let tipo: string = "";

    if (dadosComplementares.exposicaoPolitica.euMesmo)
      tipo += "1";
    if (dadosComplementares.exposicaoPolitica.parenteProximoConjugue)
      tipo += "2";
    if (dadosComplementares.exposicaoPolitica.representanteRelacionamento)
      tipo += "3";

    return tipo || null;
  }

  /* metodo responsavel por capturar o valor da profissao e retornar o nome,
 isso ocorre por que o autocomplete do IDS nao consegue compreender a disticao de comparar
 nome com valor entao capturamos o id e depois retornamos o nome no campo
*/
  public patchProfissao(Profissao: string, tipo: 'value' | 'name'): string | number | undefined {
    if (this.listprofissao) {
      return this.listprofissao.find(item => item.name === Profissao || item.value === parseInt(Profissao))?.[tipo];
    }

    return undefined;
  }

  /* metodo responsavel por capturar o valor da origem renda e retornar o nome,
 isso ocorre por que o autocomplete do IDS nao consegue compreender a disticao de comparar
 nome com valor entao capturamos o id e depois retornamos o nome no campo
*/
  public patchOrigemRenda(origemRenda: string, tipo: 'value' | 'name'): string | number | undefined {
    if (this.listaOrigemRenda)
      return this.listaOrigemRenda.find(item => item.name === origemRenda || item.value === parseInt(origemRenda))?.[tipo];

    return undefined;
  }


  /**
   * Formata uma string de data de 'dd/mm/yyyy' para 'yyyy-mm-dd'.
   * @param dateString - A string de data a ser formatada.
   * @returns A string de data formatada ou null se a entrada for inválida.
   */
  private formatDate(dateString: string): string | null {
    if (!dateString) return null;

    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}`;
  }

  /**
   * Busca o UF a partir do nome do estado.
   * @param nome - Nome do estado.
   * @returns A sigla do estado (UF) ou null se não encontrado.
   */
  public buscarUFPorNome(nome: string): string | null {
    if (!nome) return null;

    const estado = LISTA_ESTADOS_BRASIL.find((estado) => estado.name.toLowerCase() === nome.toLowerCase());
    return estado ? estado.value.toString() : null;
  }

  /**
   * Busca o nome do estado a partir do UF.
   * @param uf - Sigla do estado (UF).
   * @returns O nome do estado ou null se não encontrado.
   */
  public buscarNomePorUF(uf: string): string | null {
    if (!uf) return null;

    const estado = LISTA_ESTADOS_BRASIL.find((estado) => estado.value === uf.toUpperCase());
    return estado ? estado.name : null;
  }

  /**
   * Atualiza o telefone com DDD extraído dos dados do formulário.
   * @param formularioDadosPessoais - Dados do formulário contendo o número de celular.
   * @returns Um objeto Telefone com DDD e número atualizados.
   */
  public atualizarTelefoneComDDD(formularioDadosPessoais: { celular: string }): Telefone {
    if (!formularioDadosPessoais.celular) return null;

    const celular = formularioDadosPessoais.celular || '';
    const ddd = parseInt(celular.substring(0, 2), 10).toString() || "55"; // Default para 55
    const numero = parseInt(celular.substring(2), 10).toString() || "0";

    return {
      ddd,
      ddi: "55", // DDI fixo como 55
      numero,
    };
  }

  /**
   * Remove o traço do CEP, se presente.
   * @param cep - O CEP a ser filtrado.
   * @returns O CEP sem traço.
   */
  private filterCep(cep: string): string {
    if (!cep) return '';
    return cep.replace('-', '');
  }



  public buscarPaisSiglaNome(residenciasExterior: Array<Pais>, pais: string, tipo: 'nomePais' | 'siglaPaisDoisCaracteres'): string | number | undefined {
    if (residenciasExterior) {
      return residenciasExterior.find(item => item.nomePais === pais || item.siglaPaisDoisCaracteres === pais)?.[tipo];
    }
    return undefined;
  }

  /**
 * Remove valores nulos ou vazios do objeto proponente.
 * @param proponente - Dados do proponente.
 * @returns Objeto filtrado.
 */
  public mapperProponente(proponente: Proponente): Proponente {
    const filteredEntries = Object.entries(proponente).reduce((acc, [key, value]) => {

      if (key === 'ufNascimento' || key === 'cidadeNascimento') {
        // Permite null ou string. Se vier vazio, envia como "".
        const normalizedValue =
          value === '' || value === undefined ? "" : value;

        acc[key as keyof any] = normalizedValue as Proponente[keyof Proponente];
        return acc;
      }

      if (value === null || value === '') return acc;

      if (key === 'enderecos' && Array.isArray(value)) {
        const validEnderecos = value.filter(endereco => {
          if (endereco.cep && endereco.cep.trim() !== '') {
            if (endereco.complemento === null) {
              endereco.complemento = '';
            }
            return true;
          }
          return false;
        });

        if (validEnderecos.length > 0) {
          acc[key as keyof any] = validEnderecos as any;
        }
      } else if (key === 'documentos' && Array.isArray(value)) {
        const documentosSanitizados = value.map((documento) => ({
          ...documento,
          numero: sanitizeDocumentNumber(documento.numero || ''),
        }));

        if (documentosSanitizados.length > 0) {
          acc[key as keyof any] = documentosSanitizados as any;
        }
      } else {
        acc[key as keyof any] = value as any;
      }

      return acc;
    }, {} as Proponente);

    return filteredEntries;
  }

  private searchSiglaEmissor(orgaoEmissorSigila: string): number {
    const dados = LISTA_OPCAO_ORGAO_EMISSOR;
    return dados.valores_dominio.find(item => item.nome_resumido_valor_dominio === orgaoEmissorSigila)?.codigo_valor_dominio || 0;
  }

}